import { useEffect, useState } from 'react';

import type { GameSummary, Me } from '@/api/rest';
import { useAppRuntime } from '@/app/runtime';
import { useBackButton } from '@/telegram';
import { NoticeLine, describeApiError, type Notice } from '@/ui/notice';
import { plural } from '@/ui/plural';

export interface ProfileScreenProps {
  onBack: () => void;
}

/**
 * Профиль и история партий. В макете экрана нет: раскадровка описывает только
 * саму игру, а вход в Mini App молчаливый и человеку не показывается.
 *
 * История живёт здесь же, а не отдельным узлом: маршрут доигрового потока —
 * всё ещё черновик (docs/adr/0001-navigation.md), и плодить узлы до решения
 * по нему не стоит. Читается она сверху вниз один раз, поэтому лишний переход
 * ничего бы не дал.
 *
 * `entitlements` не показываются намеренно: биллинг заморожен, список всегда
 * пуст, и раздел «покупки» рисовать не по чему.
 */
export function ProfileScreen({ onBack }: ProfileScreenProps) {
  useBackButton(onBack);
  const restClient = useAppRuntime()?.restClient ?? null;

  const [me, setMe] = useState<Me | null>(null);
  const [meNotice, setMeNotice] = useState<Notice | null>(null);

  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [gamesNotice, setGamesNotice] = useState<Notice | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!restClient) return;
    let cancelled = false;

    restClient.me().then(
      (value) => !cancelled && setMe(value),
      (err: unknown) => !cancelled && setMeNotice(noticeFor(err)),
    );

    restClient.listGames().then(
      (page) => {
        if (cancelled) return;
        setGames(page.items);
        setCursor(page.nextCursor ?? null);
      },
      (err: unknown) => !cancelled && setGamesNotice(noticeFor(err)),
    );

    return () => {
      cancelled = true;
    };
  }, [restClient]);

  async function loadMore(): Promise<void> {
    if (!restClient || cursor === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await restClient.listGames({ cursor });
      setGames((prev) => [...(prev ?? []), ...page.items]);
      setCursor(page.nextCursor ?? null);
    } catch (err) {
      setGamesNotice(noticeFor(err));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)', paddingBottom: 'var(--clown-safe-bottom)' }}
    >
      <div className="flex-1 py-6">
        <h1 className="font-display text-task">ПРОФИЛЬ</h1>

        {!restClient && <p className="mt-2 text-base text-mute">Профиль недоступен офлайн.</p>}
        {meNotice && <NoticeLine notice={meNotice} />}
        {me && (
          <div className="mt-3 border-2 border-line bg-panel p-4">
            <p className="font-display text-row">{me.user.displayName}</p>
            <p className="mt-1 text-meta uppercase text-mute">Вход через Telegram</p>
          </div>
        )}

        <p className="mt-8 text-meta uppercase text-mute">История</p>
        {gamesNotice && <NoticeLine notice={gamesNotice} />}
        {restClient && !games && !gamesNotice && (
          <p className="mt-2 text-base text-mute">Загружаем…</p>
        )}
        {games?.length === 0 && (
          <p className="mt-2 text-base text-mute">Партий пока нет. Первая — сегодня.</p>
        )}

        {games && games.length > 0 && (
          <div className="mt-2 flex flex-col gap-rule border-2 border-line bg-line">
            {games.map((game) => (
              <GameRow key={game.id} game={game} />
            ))}
          </div>
        )}

        {cursor !== null && (
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="mt-3 min-h-tap w-full appearance-none rounded-none border-2 border-line bg-panel px-4 text-left font-display text-row disabled:text-mute"
          >
            {loadingMore ? 'Загружаем…' : 'Показать ещё'}
          </button>
        )}
      </div>
    </div>
  );
}

function GameRow({ game }: { game: GameSummary }) {
  return (
    <div className="flex min-h-row flex-col justify-center gap-1 bg-panel px-4 py-3">
      <div className="flex items-baseline gap-3">
        <span className="flex-1 text-lead">{formatDate(game.createdAt)}</span>
        <span className="text-lead text-dim">
          {game.playerCount} {plural(game.playerCount, ['игрок', 'игрока', 'игроков'])}
        </span>
      </div>
      <p className="text-base text-mute">
        {modeLabel(game.mode)} · {game.deckVersion}
        {game.endedAt ? '' : ' · не доиграна'}
      </p>
    </div>
  );
}

/**
 * Отдельной ветки «ручка не написана» здесь больше нет: `/me` и `/games` живые
 * (docs/backend-contract-changelog.md, шаг В10). Отказ истории — это отказ,
 * и показывать его надо как отказ, а не как спокойную заглушку.
 */
function noticeFor(err: unknown): Notice {
  return { text: describeApiError(err), kind: 'error' };
}

/** «14 авг, 21:47» — дата партии, а не отчёт: год и секунды тут не нужны. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function modeLabel(mode: GameSummary['mode']): string {
  return mode === 'board' ? 'бродилка' : 'свободный';
}
